import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { FiArrowLeft, FiCalendar, FiUser } from 'react-icons/fi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error('Failed to fetch blog post:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="text-white/40">Loading...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-black text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-[#17B9BE] hover:text-white transition-colors">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white" data-testid="blog-post-page">
      <section className="relative h-[55vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={post.image_url} alt={post.title} className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="mb-5">
              <span className="px-3 py-1 bg-[#17B9BE]/20 backdrop-blur-sm rounded text-xs font-semibold text-[#17B9BE] border border-[#17B9BE]/20">
                {post.category}
              </span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold mb-8 max-w-4xl mx-auto leading-tight" data-testid="post-title">
              {post.title}
            </h1>
            <div className="flex items-center justify-center text-xs text-white/40 space-x-6 uppercase tracking-wider">
              <span className="flex items-center"><FiUser className="mr-2" />{post.author}</span>
              <span className="flex items-center"><FiCalendar className="mr-2" />{formatDate(post.published_at)}</span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-white/40 hover:text-[#17B9BE] mb-12 transition-colors text-sm">
            <FiArrowLeft className="mr-2" />
            Back to Blog
          </Link>

          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="prose prose-invert prose-lg"
            dangerouslySetInnerHTML={{ __html: post.content }}
            data-testid="post-content"
          />
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
